import React, { RefObject, useState } from "react";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import ActivityComponentType from "interfaces/ActivityComponentType";
import IColumn from "interfaces/Column";
import { useRacehorse360Api } from "hooks/api";
import {
  getChunksFromResponse,
  getTotalResultsFromData
} from "utils/api-utils";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import CollapsibleSlider from "components/CollapsibleSlider";
import PaginatedList from "components/PaginatedList";
import ListTable from "components/List/ListTable/ListTable";
import Loader from "components/Loader";
import AddCustomRiskButton from "../AddCustomRiskButton";

import useStyles from "./styles";

export type TRiskListType =
  | ActivityComponentType.SLIDER
  | ActivityComponentType.PAGINATED;

interface IProps {
  type: TRiskListType;
  rootRef: RefObject<HTMLElement>;
  horse: racehorse360.IHorse;
  columns: IColumn[];
  title?: string;
  onLoad?: (
    items: racehorse360.IHorseRisk[],
    pagingInfo: racehorse360.IPagingInfo,
    alert: boolean
  ) => void;
}

const PAGE_SIZE = 5;
export const LIST_HORSE_RISK_TOTAL_RESULTS_QUERY_KEY =
  "listHorseRiskTotalResultsQueryKey";

const RiskList = (props: IProps) => {
  const { type, horse, rootRef, columns, title = "Risks", onLoad } = props;
  const horseId = horse.id;

  const classes = useStyles();
  const { currentUser } = useLoggedInUser();

  const [pages, setPages] = useState([]);

  const { useListHorseRisks } = useRacehorse360Api();

  const { data: totalData, isFetching: isTotalDataFetching } =
    useListHorseRisks(LIST_HORSE_RISK_TOTAL_RESULTS_QUERY_KEY, {
      query: {
        horseIds: [horseId]
      },
      pagingOptions: {
        maxResults: 1,
        includeSummary: true
      }
    });

  const totalResults = getTotalResultsFromData(totalData);

  const { isFetching: isFullDataFetching } = useListHorseRisks(
    {
      query: {
        horseIds: [horseId]
      },
      pagingOptions: {
        maxResults: totalResults,
        includeSummary: true
      },
      getOptions: {
        select: [
          "risk_factor.description",
          "risk_factor.is_manual",
          "risk_factor.created_by",
          "risk_factor.expiration_date",
          "identified_on"
        ]
      }
    },
    {
      enabled: Boolean(horseId),
      onSuccess: data => {
        const chunks = getChunksFromResponse(data.horseRisks);

        setPages(chunks);
        onLoad?.(data.horseRisks, data.pagingInfo, false);
      }
    }
  );

  const isDataLoading = [isTotalDataFetching, isFullDataFetching].some(Boolean);

  const getRemoveItemButtonShowingRules = (
    risk: racehorse360.IHorseRisk,
    columnIndex: number
  ) => {
    const removeRiskButtonShowingRules = [
      !columnIndex,
      risk.riskFactor?.isManual,
      !risk.riskFactor?.expirationDate ||
        new Date() < new Date(risk.riskFactor?.expirationDate)
    ];

    if (!currentUser.isChiefVet) {
      removeRiskButtonShowingRules.push(
        currentUser.rh360Id === risk.riskFactor?.createdBy?.id
      );
    }

    return removeRiskButtonShowingRules.every(Boolean);
  };

  const renderTable = (items = []) => (
    <ListTable
      tabName={title}
      items={items}
      columns={columns}
      getRemoveItemButtonShowingRules={getRemoveItemButtonShowingRules}
    />
  );

  if (isDataLoading) return <Loader />;

  if (type === ActivityComponentType.SLIDER) {
    const collapsibleSliderTitle = (
      <>
        {title}
        <AddCustomRiskButton className={classes.addCustomRiskButton} />
      </>
    );

    return (
      <CollapsibleSlider
        title={collapsibleSliderTitle}
        rootRef={rootRef}
        slides={pages}
        isFetching={isDataLoading}
        totalCount={totalResults}
        renderSlide={renderTable}
        slideSize={PAGE_SIZE}
      />
    );
  }

  return (
    <PaginatedList
      pageSize={PAGE_SIZE}
      pages={pages}
      isFetching={isDataLoading}
      renderPage={renderTable}
      totalCount={totalResults}
      noContentTitle={`No ${title}}`}
      additionalContent={
        <AddCustomRiskButton className={classes.addCustomRiskButton} />
      }
    />
  );
};

export default React.memo(RiskList);
